import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Dropdown, Layout, Menu, Button, Grid, message, Typography } from 'antd';
import SagaRaven, { SAGA_SINGLETON_PRIORITIES } from './SagaRaven';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { User as FirebaseUser } from 'firebase/auth';
import { UserOutlined } from '@ant-design/icons';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import axios from 'axios';
import { useTheme } from './theme/ThemeProvider';
import AnnouncementBanner, { AnnouncementBannerData } from './components/AnnouncementBanner';
import { RESOURCES_ROUTE } from './serviceProviderDirectory';
import { hasSeenResources, onResourcesSeenChange } from './resourcesNewness';
import './HeaderComponent.css';
import { isAgentPlatformEnabled } from './AgentPlatform/featureFlag';

const { Header } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;

export interface UsersSharedState {
  userID: string;
  accountType: number;
  credits: number;
  roles: string[];
  isLangTesterUser: boolean;
}

interface Props {
  showLoginModal: () => void;
  signOut: () => void;
  user: FirebaseUser | null;
  usersSharedState: UsersSharedState;
  /** True once the parent resolved Firebase auth state at least once */
  authResolved: boolean;
}

type BannerKind = 'announcement' | 'offer' | null;

type CreditBalanceResponse = {
  planID: string | null;
};

type BetaStatusResponse = {
  isBetaUser: boolean;
};

type AnnouncementDoc = AnnouncementBannerData & {
  startsAt?: string | null;
  endsAt?: string | null;
  updatedAt?: string | null;
};

type AnnouncementDismissalsResponse = {
  dismissedAnnouncementIds: string[];
};

//const API_BASE = 'http://localhost:8000';
const API_BASE = 'https://askpoeai-nswdzfky7q-uc.a.run.app';

const OFFER_EXPIRATION = new Date('2025-09-01');
const AGENT_PLATFORM_ENABLED = isAgentPlatformEnabled();

function isWithinWindow(a: AnnouncementDoc): boolean {
  const now = Date.now();
  const startsOk = a.startsAt ? now >= new Date(a.startsAt).getTime() : true;
  const endsOk = a.endsAt ? now <= new Date(a.endsAt).getTime() : true;
  return startsOk && endsOk;
}

const HeaderComponent: React.FC<Props> = ({ showLoginModal, signOut, user, authResolved }) => {
  const location = useLocation();
  const isCheckoutPage = location.pathname === '/checkout';
  const isManageBooksPage = location.pathname === '/manage-books';
  const screens = useBreakpoint();
  const { isDark } = useTheme();
  const [showResourcesNewIndicator, setShowResourcesNewIndicator] = useState(() => !hasSeenResources());

  const displayName = user?.displayName || (user?.email ? user.email.split('@')[0] : '');


  // 0) AUTH READY
  const authReady: boolean = authResolved;

  // 1) PLAN READY, only meaningful if signed in
  const [planReady, setPlanReady] = useState<boolean>(false);
  const [planID, setPlanID] = useState<string>('');
  const [eligibleForOffer, setEligibleForOffer] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchPlan(uid: string | undefined): Promise<void> {
      try {
        if (uid) {
          const [, creditRes] = await Promise.all([
            axios.get<BetaStatusResponse>(`${API_BASE}/check-beta-status/${uid}`).catch(() => ({ data: { isBetaUser: false } })),
            axios.get<CreditBalanceResponse>(`${API_BASE}/check_credit_balance/`, { params: { userID: uid } })
          ]);
          if (!cancelled) setPlanID(creditRes.data.planID || '');
        } else {
          if (!cancelled) setPlanID('');
        }
      } catch {
        if (!cancelled) setPlanID('');
      } finally {
        if (!cancelled) setPlanReady(true);
      }
    }

    if (authReady) {
      setPlanReady(false);
      void fetchPlan(user?.uid);
    }
    return () => {
      cancelled = true;
    };
  }, [authReady, user?.uid]);

  // Offer eligibility
  useEffect(() => {
    if (!authReady || !user) {
      setEligibleForOffer(false);
      return;
    }

    const nowBeforeExpiration = new Date() < OFFER_EXPIRATION;

    // Signed in: wait for plan to be known
    if (!planReady) {
      setEligibleForOffer(false);
      return;
    }
    const noSubscription = planID === '' || planID === 'poeTrial';
    setEligibleForOffer(nowBeforeExpiration && noSubscription);
  }, [authReady, user, planReady, planID]);

  // 2) ANNOUNCEMENT READY, resolvable only after auth is resolved
  const [announcementReady, setAnnouncementReady] = useState<boolean>(false);
  const [announcement, setAnnouncement] = useState<AnnouncementDoc | null>(null);
  const [announcementVisible, setAnnouncementVisible] = useState<boolean>(false);

  const ANNOUNCEMENT_LOCAL_DISMISS_KEY = (id: string) => `announcement.dismissed.${id}`;
  const mountedRef = useRef<boolean>(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    return onResourcesSeenChange(() => {
      setShowResourcesNewIndicator(!hasSeenResources());
    });
  }, []);

  const resourcesNavLabel = (
    <span className="app-header-nav-label">
      <span>Resources</span>
      {showResourcesNewIndicator && (
        <span className="app-header-new-indicator" aria-hidden="true">New</span>
      )}
    </span>
  );

  useEffect(() => {
    const uid = user?.uid;

    if (!authReady || !uid) {
      setAnnouncement(null);
      setAnnouncementVisible(false);
      setAnnouncementReady(false);
      return;
    }

    let cancelled = false;

    async function loadAnnouncementOnce(): Promise<void> {
      try {
        const res = await axios.get<AnnouncementDoc | { message: string }>(`${API_BASE}/announcement`);
        const maybe = res.data as AnnouncementDoc;

        if (!maybe || !maybe.id || !isWithinWindow(maybe)) {
          if (!cancelled && mountedRef.current) {
            setAnnouncement(null);
            setAnnouncementVisible(false);
          }
          return;
        }

        // Decide dismissal source based on final user state
        let visible = true;
        try {
          const d = await axios.get<AnnouncementDismissalsResponse>(`${API_BASE}/announcement-dismissals/${uid}`);
          const dismissedIds = d.data.dismissedAnnouncementIds || [];
          visible = !dismissedIds.includes(maybe.id);
        } catch {
          // Default to not showing on failure to avoid flash
          visible = false;
        }

        if (!cancelled && mountedRef.current) {
          setAnnouncement(visible ? maybe : null);
          setAnnouncementVisible(visible);
        }
      } catch {
        if (!cancelled && mountedRef.current) {
          setAnnouncement(null);
          setAnnouncementVisible(false);
        }
      } finally {
        if (!cancelled && mountedRef.current) setAnnouncementReady(true);
      }
    }

    setAnnouncementReady(false);
    void loadAnnouncementOnce();

    return () => {
      cancelled = true;
    };
  }, [authReady, user?.uid]);

  // 3) DECISION GATING
  // Only consider a banner when all of its own prerequisites are ready.
  const announcementDecisionReady = authReady && Boolean(user) && announcementReady;
  const offerDecisionReady = useMemo(() => {
    if (!authReady || !user) return false;
    return planReady;                   // signed in needs plan known
  }, [authReady, user, planReady]);

  const [offerDismissed, setOfferDismissed] = useState<boolean>(false);

  useEffect(() => {
    if (!eligibleForOffer) {
      setOfferDismissed(false);
    }
  }, [eligibleForOffer]);

  // Final decision is stable and only computed when each candidate has its own ready signal.
  const bannerToShow: BannerKind = useMemo(() => {
    // Do not render any banner while any candidate that might show is undecided.
    // Order of precedence: announcement, offer.
    if (!user) return null;
    if (!announcementDecisionReady || !offerDecisionReady) return null;

    if (announcement && announcementVisible) return 'announcement';
    if (eligibleForOffer && !offerDismissed) return 'offer';
    return null;
  }, [
    announcementDecisionReady,
    offerDecisionReady,
    announcement,
    announcementVisible,
    user,
    eligibleForOffer,
    offerDismissed
  ]);

  const handleResetPassword = async (): Promise<void> => {
    if (user && user.email) {
      try {
        const auth = getAuth();
        await sendPasswordResetEmail(auth, user.email);
        message.success('Password reset email sent');
      } catch {
        message.error('Error sending password reset email. Please try again.');
      }
    } else {
      message.error('No user logged in.');
    }
  };

  const userMenu: React.ReactElement = (
    <Menu>
      <Menu.Item key="0" disabled>
        <div>{displayName}</div>
      </Menu.Item>
      <Menu.Item key="1">
        <Link to="/account">Account</Link>
      </Menu.Item>
      {AGENT_PLATFORM_ENABLED && <Menu.Item key="agentPlatform"><Link to="/agent-platform">Agent Platform</Link></Menu.Item>}
      <Menu.Divider />
      {user && user.email ? (
        <Menu.Item key="2">
          <span onClick={handleResetPassword}>Reset Password</span>
        </Menu.Item>
      ) : null}
      <Menu.Item key="pricing">
        <Link to="/pricing">Pricing</Link>
      </Menu.Item>
      <Menu.Item key="resources">
        <Link to={RESOURCES_ROUTE}>{resourcesNavLabel}</Link>
      </Menu.Item>
      <Menu.Item key="betaAccess">
        <Link to="/betaaccess">Beta Access</Link>
      </Menu.Item>
      <Menu.Item key="3" onClick={signOut}>
        Sign Out
      </Menu.Item>
    </Menu>
  );

  const mobileMenu: React.ReactElement = (
    <Menu>
      <Menu.Item key="manageBooks"><Link to="/manage-books">My Books</Link></Menu.Item>
      <Menu.Item key="deepdive"><Link to="/deepdive">Deep Dive</Link></Menu.Item>
      <Menu.Item key="account"><Link to="/account">Account</Link></Menu.Item>
      {AGENT_PLATFORM_ENABLED && <Menu.Item key="agentPlatform"><Link to="/agent-platform">Agent Platform</Link></Menu.Item>}
      <Menu.Item key="pricing"><Link to="/pricing">Pricing</Link></Menu.Item>
      <Menu.Item key="resources"><Link to={RESOURCES_ROUTE}>{resourcesNavLabel}</Link></Menu.Item>
      <Menu.Item key="support"><Link to="/support">Support</Link></Menu.Item>
      <Menu.Item key="about"><Link to="/about">About</Link></Menu.Item>
      <Menu.Divider />
      {user && user.email ? (
        <Menu.Item key="resetPass">
          <span onClick={handleResetPassword}>Reset Password</span>
        </Menu.Item>
      ) : null}
      <Menu.Item key="betaAccess">
        <Link to="/betaaccess">Beta Access</Link>
      </Menu.Item>
      <Menu.Item key="signOut" onClick={signOut}>Sign Out</Menu.Item>
    </Menu>
  );

  const mobileGuestMenu: React.ReactElement = (
    <Menu>
      <Menu.Item key="pricing"><Link to="/pricing">Pricing</Link></Menu.Item>
      <Menu.Item key="resources"><Link to={RESOURCES_ROUTE}>{resourcesNavLabel}</Link></Menu.Item>
      <Menu.Item key="support"><Link to="/support">Support</Link></Menu.Item>
      <Menu.Item key="about"><Link to="/about">About</Link></Menu.Item>
      <Menu.Divider />
      <Menu.Item key="getStarted" onClick={showLoginModal}>Get Started</Menu.Item>
    </Menu>
  );

  return (
    <>
      {!isCheckoutPage && bannerToShow === 'announcement' && announcement && (
        <AnnouncementBanner
          data={announcement}
          onClose={async () => {
            try {
              if (user?.uid) {
                await axios.post(`${API_BASE}/dismiss-announcement/${user.uid}`, {
                  announcementId: announcement.id,
                  dismissedAt: new Date().toISOString(),
                });
              } else {
                localStorage.setItem(ANNOUNCEMENT_LOCAL_DISMISS_KEY(announcement.id), '1');
              }
            } catch {
              // ignore
            } finally {
              setAnnouncementVisible(false);
              setAnnouncement(null);
            }
          }}
        />
      )}

      {!isCheckoutPage && bannerToShow === 'offer' && (
        <AnnouncementBanner
          data={{
            id: 'offer-banner',
            title: 'Special Offer',
            message: 'Use the coupon code SCRIBE20 for 20% off your first month.',
            type: 'info',
            appearance: {
              align: 'center',
              showIcon: false,
              closable: true,
              variant: 'announcement',
            },
            body: (
              <div style={{ textAlign: 'center' }}>
                Use the coupon code <Text strong>SCRIBE20</Text> for 20% off your first month.
                <br />
                <Link to="/account">Subscribe Today</Link>
              </div>
            ),
          }}
          onClose={() => setOfferDismissed(true)}
        />
      )}

      <Header
        className={[
          'app-header',
          isCheckoutPage ? 'app-header--checkout' : '',
          isManageBooksPage ? 'app-header--sticky' : ''
        ].filter(Boolean).join(' ')}
      >
        <div className="app-header-inner">
          <div className="app-header-brand">
            <img
              className="app-header-logo"
              src={isDark ? '/poelogo_dm.png' : '/poelogo.png'}
              alt="ScribeShadow"
            />
          </div>

          {!isCheckoutPage && screens.md && (
            <nav className="app-header-nav" aria-label="Primary navigation">
              <NavLink
                to="/manage-books"
                className={({ isActive }) => `app-header-nav-link ${isActive ? 'is-active' : ''}`}
              >
                My Books
              </NavLink>
              {user && <NavLink to="/deepdive"
                className={({ isActive }) => `app-header-nav-link ${isActive ? 'is-active' : ''}`}>
                Deep Dive
              </NavLink>}
              {user ? (
                <NavLink
                  to="/account"
                  className={({ isActive }) => `app-header-nav-link ${isActive ? 'is-active' : ''}`}
                >
                  Account
                </NavLink>
              ) : (
                <NavLink
                  to="/pricing"
                  className={({ isActive }) => `app-header-nav-link ${isActive ? 'is-active' : ''}`}
                >
                  Pricing
                </NavLink>
              )}
              {user && AGENT_PLATFORM_ENABLED && (
                <NavLink to="/agent-platform" className={({ isActive }) => `app-header-nav-link ${isActive ? 'is-active' : ''}`}>
                  Agent Platform
                </NavLink>
              )}
              <NavLink
                to={RESOURCES_ROUTE}
                className={({ isActive }) => `app-header-nav-link ${isActive ? 'is-active' : ''}`}
              >
                {resourcesNavLabel}
              </NavLink>
              <NavLink
                to="/support"
                className={({ isActive }) => `app-header-nav-link ${isActive ? 'is-active' : ''}`}
              >
                Support
              </NavLink>
              <NavLink
                to="/about"
                className={({ isActive }) => `app-header-nav-link ${isActive ? 'is-active' : ''}`}
              >
                About
              </NavLink>
            </nav>
          )}

          {!isCheckoutPage && (
            <div className="app-header-actions">
              {screens.md && user ? (
                <div className="app-header-user-cluster">
                  <span className="app-header-raven-slot" aria-hidden="true">
                    <SagaRaven
                      className="app-header-raven"
                      size="xs"
                      idleMixer
                      nightcapAuto
                      mirror
                      priority={SAGA_SINGLETON_PRIORITIES.ambient}
                      style={{ pointerEvents: 'none' }}
                    />
                  </span>
                  <Dropdown overlay={userMenu} placement="bottomRight">
                    <Avatar className="app-header-avatar">{(displayName.charAt(0) || 'U').toUpperCase()}</Avatar>
                  </Dropdown>
                </div>
              ) : screens.md ? (
                <Button onClick={showLoginModal}>Get Started</Button>
              ) : user ? (
                <Dropdown overlay={mobileMenu} trigger={['click']}>
                  <Button
                    className="app-header-mobile-menu-button"
                    icon={<UserOutlined />}
                    aria-label="Open account menu"
                  />
                </Dropdown>
              ) : (
                <Dropdown overlay={mobileGuestMenu} trigger={['click']}>
                  <Button
                    className="app-header-mobile-menu-button"
                    icon={<UserOutlined />}
                    aria-label="Open navigation menu"
                  />
                </Dropdown>
              )}
            </div>
          )}
        </div>
      </Header>
    </>
  );
};

export default HeaderComponent;
